import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { ChargeQuestionCountsDto, TelemetryOverviewDto, TelemetryRunDetailDto, TelemetryRunListParams, TelemetryRunPageDto } from '@signalvote/shared';
import { authService } from '../../services/authService';
import { apiJson } from './apiFetch';
import { buildQueryString } from './queryUtils';
import { queryKeys, type TelemetryOverviewParams } from '../../lib/queryKeys';

const BASE = '/api/admin/assistant-telemetry';

/** Platform-admin only (the server 403s anyone else) and subsidiary-wide, so not gated on a selected campaign. */
export function useAssistantTelemetryOverview(params: TelemetryOverviewParams) {
  return useQuery({
    queryKey: queryKeys.assistantTelemetry.overview(params),
    queryFn: () =>
      apiJson<TelemetryOverviewDto>(`${BASE}/overview${buildQueryString(params)}`, {
        error: 'Failed to fetch assistant telemetry',
      }),
    enabled: authService.isAuthenticated(),
    placeholderData: keepPreviousData,
  });
}

export function useAssistantTelemetryRuns(params: TelemetryRunListParams) {
  return useQuery({
    queryKey: queryKeys.assistantTelemetry.runs(params),
    queryFn: () =>
      apiJson<TelemetryRunPageDto>(
        `${BASE}/runs${buildQueryString({ ...params, page: params.page?.toString() })}`,
        { error: 'Failed to fetch assistant runs' },
      ),
    enabled: authService.isAuthenticated(),
    placeholderData: keepPreviousData,
  });
}

export function useAssistantTelemetryRun(runId: string) {
  return useQuery({
    queryKey: queryKeys.assistantTelemetry.run(runId),
    queryFn: () =>
      apiJson<TelemetryRunDetailDto>(`${BASE}/runs/${encodeURIComponent(runId)}`, {
        error: 'Failed to fetch the assistant run',
      }),
    enabled: authService.isAuthenticated(),
  });
}

export function useAssistantChargeQuestions(params: TelemetryOverviewParams) {
  return useQuery({
    queryKey: queryKeys.assistantTelemetry.chargeQuestions(params),
    queryFn: () =>
      apiJson<ChargeQuestionCountsDto>(`${BASE}/charge-questions${buildQueryString(params)}`, {
        error: 'Failed to fetch charge question counts',
      }),
    enabled: authService.isAuthenticated(),
    placeholderData: keepPreviousData,
  });
}
